import { Suspense, lazy, ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SeekAuthProvider } from "@/contexts/SeekAuthContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SeekProtectedRoute } from "@/components/seek/SeekProtectedRoute";
import { SuperAdminProtectedRoute } from "@/components/super-admin/SuperAdminProtectedRoute";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { DomainRedirect } from "@/components/DomainRouter";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PortalDomainGuard } from "@/components/PortalDomainGuard";
import { PortalTabBranding } from "@/components/PortalTabBranding";
import { resolvePortal } from "@/lib/portalTabBranding";

// Keep core auth/high-traffic portal pages eager to avoid preview route-fetch failures
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Jobs from "./pages/Jobs";
import Candidates from "./pages/Candidates";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import MyAccount from "./pages/MyAccount";
import OrganizationProfile from "./pages/OrganizationProfile";
import Interviews from "./pages/Interviews";
import TeamManagement from "./pages/TeamManagement";
import TalentDiscovery from "./pages/TalentDiscovery";
import InvitationsManagement from "./pages/InvitationsManagement";
import Notifications from "./pages/Notifications";
import UserGuide from "./pages/UserGuide";
import BillingSettings from "./pages/BillingSettings";
import SeekDashboard from "./pages/seek/SeekDashboard";
import SeekSubscription from "./pages/seek/SeekSubscription";
import CustomerInvoice from "./pages/CustomerInvoice";
import seekLogo from "@/assets/logo-seek-light.svg";
import forkLogo from "@/assets/logo-light.svg";
import SeekProfile from "./pages/seek/SeekProfile";
import SeekApplications from "./pages/seek/SeekApplications";
import SAOrganizations from "./pages/super-admin/SAOrganizations";
import SAEnterpriseDeals from "./pages/super-admin/SAEnterpriseDeals";

type LazyComponent = ComponentType<never>;
type LazyModule<T extends LazyComponent> = { default: T };

const IMPORT_RETRY_COUNT = 4;
const IMPORT_RETRY_DELAY_MS = 1500;

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getFailedImportUrl(error: unknown) {
  if (!(error instanceof Error)) return null;

  const prefix = "Failed to fetch dynamically imported module:";
  const url = error.message.includes(prefix)
    ? error.message.split(prefix)[1]?.trim()
    : null;

  return url || null;
}

async function importFromUrl<T extends LazyComponent>(url: string): Promise<LazyModule<T>> {
  return (await import(/* @vite-ignore */ url)) as LazyModule<T>;
}

// Retry wrapper for dynamic imports — handles transient Vite dev-server fetch failures
async function retryImport<T extends LazyComponent>(
  factory: () => Promise<LazyModule<T>>,
  retries = IMPORT_RETRY_COUNT,
): Promise<LazyModule<T>> {
  try {
    return await factory();
  } catch (error) {
    const failedUrl = getFailedImportUrl(error);

    if (retries > 0) {
      await delay(IMPORT_RETRY_DELAY_MS);

      if (failedUrl?.includes("/src/")) {
        try {
          const retryUrl = new URL(failedUrl);
          retryUrl.searchParams.set("importRetry", `${Date.now()}`);
          return await importFromUrl<T>(retryUrl.href);
        } catch {
          // Fall back to the original factory if the cache-busted retry also fails.
        }
      }

      return retryImport(factory, retries - 1);
    }

    window.location.reload();
    throw error;
  }
}

function lazyRetry<T extends LazyComponent>(factory: () => Promise<LazyModule<T>>) {
  return lazy<T>(() => retryImport(factory));
}

const PageLoader = () => {
  const { t } = useTranslation();
  const pathname = typeof window === "undefined" ? "" : window.location.pathname;
  const isSeek = pathname.startsWith("/seek");
  const isBilling = pathname.includes("/settings/subscription") || pathname.includes("/billing");
  const logo = isSeek ? seekLogo : forkLogo;
  const accentClass = isSeek ? "bg-seek" : "bg-primary";
  const tintClass = isSeek ? "bg-seek/10" : "bg-primary/10";
  const ringClass = isSeek ? "border-seek/25" : "border-primary/25";
  const label = isBilling
    ? t("common.loadingBilling")
    : isSeek
      ? t("common.loadingForkSeek")
      : t("common.loadingFork");
  const detail = isBilling ? t("common.loadingBillingDetails") : t("common.loadingWorkspace");

  return (
    <div className="min-h-screen bg-background px-5 py-8">
      <div className="mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-5xl flex-col justify-center gap-8">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tintClass}`}>
            <img src={logo} alt="" className="h-7 w-7 object-contain" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{label}</p>
            <p className="text-xs text-muted-foreground">{detail}</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className={`h-1.5 w-full ${tintClass}`}>
            <div className={`h-full w-1/3 ${accentClass} animate-pulse`} />
          </div>
          <div className="grid gap-0 md:grid-cols-[1.15fr_0.85fr]">
            <div className="space-y-5 border-border p-5 md:border-e">
              <div className="space-y-2">
                <div className={`h-4 w-36 rounded-full ${tintClass}`} />
                <div className="h-8 w-64 max-w-full rounded-md bg-muted" />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {[0, 1, 2].map((item) => (
                  <div key={item} className={`h-24 rounded-lg border ${ringClass} ${tintClass}`} />
                ))}
              </div>
            </div>
            <div className="space-y-3 p-5">
              <div className="h-4 w-28 rounded-full bg-muted" />
              <div className="h-20 rounded-lg bg-muted" />
              <div className="h-20 rounded-lg bg-muted" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Public pages ───────────────────────────────────────────
const Landing = lazyRetry(() => import("./pages/Landing"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const VerifyEmailChange = lazyRetry(() => import("./pages/VerifyEmailChange"));
const AuthGoogleCallback = lazyRetry(() => import("./pages/AuthGoogleCallback"));
const PublicApply = lazyRetry(() => import("./pages/PublicApply"));
const CandidateInterview = lazyRetry(() => import("./pages/CandidateInterview"));
const F2FInvitation = lazyRetry(() => import("./pages/F2FInvitation"));
const OfferAcceptance = lazyRetry(() => import("./pages/OfferAcceptance"));
const ContractSigning = lazyRetry(() => import("./pages/ContractSigning"));
const QaHub = lazyRetry(() => import("./pages/QaHub"));
const QaChecklist = lazyRetry(() => import("./pages/QaChecklist"));
const QaChecklistSeek = lazyRetry(() => import("./pages/QaChecklistSeek"));
const QaChecklistAdmin = lazyRetry(() => import("./pages/QaChecklistAdmin"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const VerifyEmail = lazyRetry(() => import("./pages/VerifyEmail"));

// ─── ForkHire pages ─────────────────────────────────────────
const JobDetail = lazyRetry(() => import("./pages/JobDetailV2"));
const CandidateDetail = lazyRetry(() => import("./pages/CandidateDetail"));
const InterviewConfig = lazyRetry(() => import("./pages/InterviewConfig"));
const ChangePassword = lazyRetry(() => import("./pages/ChangePassword"));
const ForceResetPassword = lazyRetry(() => import("./pages/ForceResetPassword"));
const BillingCredits = lazyRetry(() => import("./pages/BillingCredits"));
const BillingInvoices = lazyRetry(() => import("./pages/BillingInvoices"));
const BillingIdentity = lazyRetry(() => import("./pages/BillingIdentity"));
const CompleteOrganizationSetup = lazyRetry(() => import("./pages/CompleteOrganizationSetup"));
const CompleteCompanySetup = lazyRetry(() => import("./pages/hire/CompleteCompanySetup"));

// ─── Super Admin pages ──────────────────────────────────────
const AdminAuth = lazyRetry(() => import("./pages/super-admin/AdminAuth"));
const SADashboard = lazyRetry(() => import("./pages/super-admin/SADashboard"));
const SAServices = lazyRetry(() => import("./pages/super-admin/SAServices"));
const SAPlans = lazyRetry(() => import("./pages/super-admin/SAPlans"));
const SACatalog = lazyRetry(() => import("./pages/super-admin/SACatalog"));
const SAAiUsage = lazyRetry(() => import("./pages/super-admin/SAAiUsage"));
const SAAiPrompts = lazyRetry(() => import("./pages/super-admin/SAAiPrompts"));
const SACvDesignLibrary = lazyRetry(() => import("./pages/super-admin/SACvDesignLibrary"));
const SACvDesignEditor = lazyRetry(() => import("./pages/super-admin/SACvDesignEditor"));
const SAEmailTemplates = lazyRetry(() => import("./pages/super-admin/SAEmailTemplates"));
const SAConsumptionOverview = lazyRetry(() => import("./pages/super-admin/SAConsumptionOverview"));
const SAIdentityRequests = lazyRetry(() => import("./pages/super-admin/SAIdentityRequests"));
const SAFeatureFlags = lazyRetry(() => import("./pages/super-admin/SAFeatureFlags"));
const SAActionLog = lazyRetry(() => import("./pages/super-admin/SAActionLog"));
const SAAlerts = lazyRetry(() => import("./pages/super-admin/SAAlerts"));

const SAAddOns = lazyRetry(() => import("./pages/super-admin/SAAddOns"));
const SACreditPacks = lazyRetry(() => import("./pages/super-admin/SACreditPacks"));
const SACredits = lazyRetry(() => import("./pages/super-admin/SACredits"));
const SAServiceCreditPricing = lazyRetry(() => import("./pages/super-admin/SAServiceCreditPricing"));
const SASubscriptions = lazyRetry(() => import("./pages/super-admin/SASubscriptions"));
const SASystemIssues = lazyRetry(() => import("./pages/super-admin/SASystemIssues"));
const SAShadowAccess = lazyRetry(() => import("./pages/super-admin/SAShadowAccess"));
const SAOrganizationDetail = lazyRetry(() => import("./pages/super-admin/SAOrganizationDetail"));
const SACandidates = lazyRetry(() => import("./pages/super-admin/SACandidates"));
const SACandidateDetail = lazyRetry(() => import("./pages/super-admin/SACandidateDetail"));
const SAAiFailures = lazyRetry(() => import("./pages/super-admin/SAAiFailures"));
const SAPaymentSimulator = lazyRetry(() => import("./pages/super-admin/SAPaymentSimulator"));
const SAInvoices = lazyRetry(() => import("./pages/super-admin/SAInvoices"));
const SAFinanceTools = lazyRetry(() => import("./pages/super-admin/SAFinanceTools"));
const SAIntroPricing = lazyRetry(() => import("./pages/super-admin/SAIntroPricing"));
const SATrials = lazyRetry(() => import("./pages/super-admin/SATrials"));
const SABanners = lazyRetry(() => import("./pages/super-admin/SABanners"));
const SAGovernanceOps = lazyRetry(() => import("./pages/super-admin/SAGovernanceOps"));
const SAErrorEvents = lazyRetry(() => import("./pages/super-admin/SAErrorEvents"));
const SATestIssues = lazyRetry(() => import("./pages/super-admin/SATestIssues"));
const SAGovernanceHealth = lazyRetry(() => import("./pages/super-admin/SAGovernanceHealth"));
const SAGovernanceInspect = lazyRetry(() => import("./pages/super-admin/SAGovernanceInspect"));
const SAPaymentRecovery = lazyRetry(() => import("./pages/super-admin/SAPaymentRecovery"));
const SARefunds = lazyRetry(() => import("./pages/super-admin/SARefunds"));
const SAStalePending = lazyRetry(() => import("./pages/super-admin/SAStalePending"));
const SAPaymentReliability = lazyRetry(() => import("./pages/super-admin/SAPaymentReliability"));
const SAPlanServiceGrid = lazyRetry(() => import("./pages/super-admin/SAPlanServiceGrid"));
const SAPlanServiceDetail = lazyRetry(() => import("./pages/super-admin/SAPlanServiceDetail"));
const SADeletionReviews = lazyRetry(() => import("./pages/super-admin/SADeletionReviews"));

// ─── ForkSeek pages ─────────────────────────────────────────
const SeekAuth = lazyRetry(() => import("./pages/seek/SeekAuth"));
const SeekGoogleCallback = lazyRetry(() => import("./pages/seek/SeekGoogleCallback"));
const SeekSignup = lazyRetry(() => import("./pages/seek/SeekSignup"));
const SeekVerifyEmail = lazyRetry(() => import("./pages/seek/SeekVerifyEmail"));
const SeekCv = lazyRetry(() => import("./pages/seek/SeekCv"));
const SeekJobs = lazyRetry(() => import("./pages/seek/SeekJobs"));
const SeekApplicationDetail = lazyRetry(() => import("./pages/seek/SeekApplicationDetail"));
const SeekNotifications = lazyRetry(() => import("./pages/seek/SeekNotifications"));
const SeekSettings = lazyRetry(() => import("./pages/seek/SeekSettings"));
const SeekInterviewPractice = lazyRetry(() => import("./pages/seek/SeekInterviewPractice"));
const SeekPracticeSession = lazyRetry(() => import("./pages/seek/SeekPracticeSession"));
const SeekSavedJobs = lazyRetry(() => import("./pages/seek/SeekSavedJobs"));
const SeekOnboarding = lazyRetry(() => import("./pages/seek/SeekOnboarding"));
const SeekAccount = lazyRetry(() => import("./pages/seek/SeekAccount"));
const SeekCredits = lazyRetry(() => import("./pages/seek/SeekCredits"));
const SeekInvoices = lazyRetry(() => import("./pages/seek/SeekInvoices"));

const queryClient = new QueryClient();

/** Layout wrapper: provides AuthProvider to all ForkHire routes */
const ForkHireLayout = () => (
  <ErrorBoundary portal="ForkHire">
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  </ErrorBoundary>
);

/** Layout wrapper: provides SeekAuthProvider to all ForkSeek routes  */
const ForkSeekLayout = () => (
  <ErrorBoundary portal="ForkSeek">
    <SeekAuthProvider>
      <Outlet />
    </SeekAuthProvider>
  </ErrorBoundary>
);

/** Layout wrapper: provides AdminAuthProvider to all Super Admin routes */
const AdminLayout = () => (
  <ErrorBoundary portal="Super Admin">
    <AdminAuthProvider>
      <Outlet />
    </AdminAuthProvider>
  </ErrorBoundary>
);

/** The same clean /invoices route is portal-scoped by the current host. */
const CustomerInvoicePortalRoute = () => {
  const portal = resolvePortal(
    window.location.pathname,
    window.location.search,
    window.location.hostname,
  );

  if (portal === "seek") {
    return (
      <ErrorBoundary portal="ForkSeek">
        <SeekAuthProvider>
          <SeekProtectedRoute><CustomerInvoice portal="seek" /></SeekProtectedRoute>
        </SeekAuthProvider>
      </ErrorBoundary>
    );
  }

  if (portal === "hire") {
    return (
      <ErrorBoundary portal="ForkHire">
        <AuthProvider>
          <ProtectedRoute><CustomerInvoice portal="hire" /></ProtectedRoute>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  return <Navigate to="/" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PortalTabBranding />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes — no auth provider */}
            <Route path="/" element={<DomainRedirect><Landing /></DomainRedirect>} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/reset-password" element={<Navigate to="/hire/auth" replace />} />
            <Route path="/hire/reset-password" element={<ResetPassword portal="hire" purpose="recovery" />} />
            <Route path="/seek/reset-password" element={<ResetPassword portal="seek" purpose="recovery" />} />
            <Route path="/hire/accept-invite" element={<ResetPassword portal="hire" purpose="invite" />} />
            <Route path="/admin/accept-invite" element={<ResetPassword portal="admin" purpose="invite" />} />
            <Route path="/hire/verify-email-change" element={<VerifyEmailChange portal="hire" />} />
            <Route path="/seek/verify-email-change" element={<VerifyEmailChange portal="seek" />} />
            <Route path="/apply/:slug" element={<PublicApply />} />
            <Route path="/interview/:token" element={<CandidateInterview />} />
            <Route path="/f2f/:token" element={<F2FInvitation />} />
            <Route path="/offer/:token" element={<OfferAcceptance />} />
            <Route path="/contract-signing" element={<ContractSigning />} />
            <Route path="/invoices/:invoiceNumberSlug" element={<CustomerInvoicePortalRoute />} />
            <Route path="/qa" element={<QaHub />} />
            <Route path="/qa-checklist" element={<QaChecklist />} />
            <Route path="/qa-checklist-seek" element={<QaChecklistSeek />} />
            <Route path="/qa-checklist-admin" element={<QaChecklistAdmin />} />

            {/* ForkHire portal — single shared AuthProvider via layout */}
            <Route element={<PortalDomainGuard portal="hire"><ForkHireLayout /></PortalDomainGuard>}>
              <Route path="/hire/auth" element={<Auth />} />
              <Route path="/hire/auth/callback" element={<AuthGoogleCallback />} />
              <Route path="/hire/complete-company-setup" element={<CompleteCompanySetup />} />
              <Route path="/hire/verify-email" element={<VerifyEmail />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
              <Route path="/jobs/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
              <Route path="/interviews" element={<ProtectedRoute><Interviews /></ProtectedRoute>} />
              <Route path="/interviews/:jobId" element={<ProtectedRoute><InterviewConfig /></ProtectedRoute>} />
              <Route path="/candidates" element={<ProtectedRoute><Candidates /></ProtectedRoute>} />
              <Route path="/candidates/:id" element={<ProtectedRoute><CandidateDetail /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/settings/billing" element={<ProtectedRoute><BillingSettings /></ProtectedRoute>} />
              <Route path="/settings/billing/credits" element={<ProtectedRoute><BillingCredits /></ProtectedRoute>} />
              <Route path="/settings/billing/invoices" element={<ProtectedRoute><BillingInvoices /></ProtectedRoute>} />
              <Route path="/settings/billing/identity" element={<ProtectedRoute><BillingIdentity /></ProtectedRoute>} />
              <Route path="/checkout/:planSlug" element={<Navigate to="/settings/billing" replace />} />
              <Route path="/organization" element={<ProtectedRoute><OrganizationProfile /></ProtectedRoute>} />
              <Route path="/organization-setup" element={<ProtectedRoute><CompleteOrganizationSetup /></ProtectedRoute>} />
              <Route path="/my-account" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
              <Route path="/talent" element={<ProtectedRoute><TalentDiscovery /></ProtectedRoute>} />
              <Route path="/talent/invitations" element={<ProtectedRoute><InvitationsManagement /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/user-guide" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
              <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
              <Route path="/force-reset-password" element={<ProtectedRoute><ForceResetPassword /></ProtectedRoute>} />
            </Route>

            {/* ForkSeek portal — single shared SeekAuthProvider via layout */}
            <Route path="/seek" element={<PortalDomainGuard portal="seek"><ForkSeekLayout /></PortalDomainGuard>}>
              <Route path="auth" element={<SeekAuth />} />
              <Route path="signin" element={<SeekAuth />} />
              <Route path="auth/callback" element={<SeekGoogleCallback />} />
              <Route path="signup" element={<SeekSignup />} />
              <Route path="verify-email" element={<SeekVerifyEmail />} />
              <Route path="onboarding" element={<SeekProtectedRoute><SeekOnboarding /></SeekProtectedRoute>} />
              <Route index element={<SeekProtectedRoute><SeekDashboard /></SeekProtectedRoute>} />
              <Route path="dashboard" element={<SeekProtectedRoute><SeekDashboard /></SeekProtectedRoute>} />
              <Route path="profile" element={<SeekProtectedRoute><SeekProfile /></SeekProtectedRoute>} />
              <Route path="cv" element={<SeekProtectedRoute><SeekCv /></SeekProtectedRoute>} />
              <Route path="jobs" element={<SeekProtectedRoute><SeekJobs /></SeekProtectedRoute>} />
              <Route path="saved-jobs" element={<SeekProtectedRoute><SeekSavedJobs /></SeekProtectedRoute>} />
              <Route path="applications" element={<SeekProtectedRoute><SeekApplications /></SeekProtectedRoute>} />
              <Route path="applications/:applicationId" element={<SeekProtectedRoute><SeekApplicationDetail /></SeekProtectedRoute>} />
              <Route path="notifications" element={<SeekProtectedRoute><SeekNotifications /></SeekProtectedRoute>} />
              <Route path="settings" element={<SeekProtectedRoute><SeekSettings /></SeekProtectedRoute>} />
              <Route path="settings/subscription" element={<SeekProtectedRoute><SeekSubscription /></SeekProtectedRoute>} />
              <Route path="settings/credits" element={<SeekProtectedRoute><SeekCredits /></SeekProtectedRoute>} />
              <Route path="settings/invoices" element={<SeekProtectedRoute><SeekInvoices /></SeekProtectedRoute>} />
              <Route path="checkout/:planSlug" element={<Navigate to="/seek/settings/subscription" replace />} />
              <Route path="interview-practice" element={<SeekProtectedRoute><SeekInterviewPractice /></SeekProtectedRoute>} />
              <Route path="interview-practice/:sessionId" element={<SeekProtectedRoute><SeekPracticeSession /></SeekProtectedRoute>} />
              <Route path="account" element={<SeekProtectedRoute><SeekAccount /></SeekProtectedRoute>} />
            </Route>

            {/* Super Admin portal — isolated AdminAuthProvider */}
            <Route element={<PortalDomainGuard portal="admin"><AdminLayout /></PortalDomainGuard>}>
              <Route path="/admin/auth" element={<AdminAuth />} />
              <Route path="/super-admin" element={<SuperAdminProtectedRoute><SuperAdminLayout /></SuperAdminProtectedRoute>}>
                <Route index element={<SADashboard />} />
                <Route path="services" element={<SAServices />} />
                <Route path="plans" element={<SAPlans />} />
                <Route path="catalog" element={<SACatalog />} />
                <Route path="plan-services" element={<Navigate to="/super-admin/plan-service-grid" replace />} />
                <Route path="plan-service-grid" element={<SAPlanServiceGrid />} />
                <Route path="plan-services/:serviceId" element={<SAPlanServiceDetail />} />
                <Route path="organizations" element={<SAOrganizations />} />
                <Route path="ai-usage" element={<SAAiUsage />} />
                <Route path="ai-prompts" element={<SAAiPrompts />} />
                <Route path="cv-design-library" element={<SACvDesignLibrary />} />
                <Route path="cv-design-library/editor" element={<SACvDesignEditor />} />
                <Route path="email-templates" element={<SAEmailTemplates />} />
                <Route path="consumption-overview" element={<SAConsumptionOverview />} />
                <Route path="identity-requests" element={<SAIdentityRequests />} />
                <Route path="feature-flags" element={<SAFeatureFlags />} />
                <Route path="action-log" element={<SAActionLog />} />
                <Route path="alerts" element={<SAAlerts />} />
                
                <Route path="add-ons" element={<SAAddOns />} />
                <Route path="credit-packs" element={<SACreditPacks />} />
                <Route path="service-credit-pricing" element={<SAServiceCreditPricing />} />
                <Route path="enterprise-deals" element={<SAEnterpriseDeals />} />
                <Route path="credits" element={<SACredits />} />
                <Route path="subscriptions" element={<SASubscriptions />} />
                <Route path="system-issues" element={<SASystemIssues />} />
                <Route path="error-events" element={<SAErrorEvents />} />
                <Route path="test-issues" element={<SATestIssues />} />
                <Route path="shadow-access" element={<SAShadowAccess />} />
                <Route path="organizations/:orgId" element={<SAOrganizationDetail />} />
                <Route path="candidates" element={<SACandidates />} />
                <Route path="candidates/:candidateId" element={<SACandidateDetail />} />
                <Route path="ai-failures" element={<SAAiFailures />} />
                <Route path="payment-simulator" element={<SAPaymentSimulator />} />
                <Route path="invoices" element={<SAInvoices />} />
                <Route path="finance-tools" element={<SAFinanceTools />} />
                <Route path="payment-recovery" element={<SAPaymentRecovery />} />
                <Route path="payment-reliability" element={<SAPaymentReliability />} />
                <Route path="refunds" element={<SARefunds />} />
                <Route path="stale-pending" element={<SAStalePending />} />
                <Route path="intro-pricing" element={<SAIntroPricing />} />
                <Route path="trials" element={<SATrials />} />
                <Route path="banners" element={<SABanners />} />
                <Route path="governance-ops" element={<SAGovernanceOps />} />
                <Route path="governance-health" element={<SAGovernanceHealth />} />
                <Route path="governance-inspect" element={<SAGovernanceInspect />} />
                <Route path="deletion-reviews" element={<SADeletionReviews />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
